/** @jsxImportSource @emotion/react */

import { ChampionshipEditTab } from 'modules/championship/domain/enums/championship-edit-tab';
import { Pages } from 'modules/navigation/domain/enums/pages';
import { announcedChampionshipTeamsDataSelector } from 'modules/teams/store/championship-teams/selectors';
import { championshipIsLoadingSelector } from 'modules/championship/store/championship/selectors';
import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useMatch, useParams } from 'react-router-dom';
import UDBreadcrumbs from 'ud-ui/components/breadcrumbs';
import { useMainLayout } from 'modules/app-shell/ui/layout/context';
import ChampionshipEditForm from 'modules/championship/ui/components/edit-form';
import ChampionshipEditFormTabs from 'modules/championship/ui/components/edit-form/tabs';
import { useChampionship } from 'modules/championship/ui/hooks/use-championship';
import { useChampionshipFetch } from 'modules/championship/ui/hooks/use-championship-fetch';

const ChampionshipEditPage = () => {
  const { id: champId } = useParams();
  useChampionshipFetch();

  const { data: champ } = useChampionship(Number(champId));
  const champTeamsData = useSelector(announcedChampionshipTeamsDataSelector(champ?.id));
  const isChampionshipLoading = useSelector(championshipIsLoadingSelector);
  const { setHeaderContent, setContentClassName } = useMainLayout();

  const applicationsPageMatch = useMatch(`${Pages.CHAMPIONSHIPS.EDIT}/applications/*`);
  const applicationPlayersPageMatch = useMatch(`${Pages.CHAMPIONSHIPS.EDIT}/applications/:teamId`);
  const teamPlayersPageRules = useMatch(`${Pages.CHAMPIONSHIPS.EDIT}/rules`);
  const teamPlayersPageSteps = useMatch(`${Pages.CHAMPIONSHIPS.EDIT}/steps`);
  const teamPlayersPageSymbolicTeams = useMatch(`${Pages.CHAMPIONSHIPS.EDIT}/symbolic-teams`);

  const breadcrumbsItems = useMemo(() => {
    const items = [{ label: 'Турниры', to: Pages.CHAMPIONSHIPS.INDEX }];

    if (!champ) {
      if (isChampionshipLoading) {
        items.push({
          label: 'Загрузка...',
          to: Pages.CHAMPIONSHIPS.builders.edit(Number(champId)),
        });
      }
      return items;
    }

    items.push({
      label: champ.name,
      to: Pages.CHAMPIONSHIPS.builders.edit(champ.id),
    });

    if (applicationsPageMatch) {
      items.push({
        label: 'Заявки',
        to: Pages.CHAMPIONSHIPS.builders.edit(champ.id, ChampionshipEditTab.TEAMS),
      });
    }

    if (teamPlayersPageRules) {
      items.push({
        label: 'Условия',
        to: Pages.CHAMPIONSHIPS.builders.edit(champ.id, 'rules'),
      });
    }

    if (teamPlayersPageSteps) {
      items.push({
        label: 'Этапы',
        to: Pages.CHAMPIONSHIPS.builders.edit(champ.id, 'steps'),
      });
    }

    if (teamPlayersPageSymbolicTeams) {
      items.push({
        label: 'Сборные',
        to: Pages.CHAMPIONSHIPS.builders.edit(champ.id, 'symbolic-teams'),
      });
    }

    if (applicationPlayersPageMatch && applicationPlayersPageMatch.params.teamId && champTeamsData) {
      const teamId = parseInt(applicationPlayersPageMatch.params.teamId);
      const champTeam = champTeamsData[teamId];

      if (champTeam) {
        items.push({
          label: champTeam.name,
          to: Pages.CHAMPIONSHIPS.builders.edit(champ.id, `${ChampionshipEditTab.TEAMS}/${champTeam.id}`),
        });
      }
    }

    return items;
  }, [
    champ,
    applicationsPageMatch,
    teamPlayersPageRules,
    teamPlayersPageSteps,
    teamPlayersPageSymbolicTeams,
    applicationPlayersPageMatch,
    champTeamsData,
    isChampionshipLoading,
    champId,
  ]);

  const breadcrumbs = useMemo(() => {
    return <UDBreadcrumbs className="my-10" breadcrumbs={breadcrumbsItems} />;
  }, [breadcrumbsItems]);

  useEffect(() => {
    setContentClassName('flex flex-col');
    setHeaderContent(breadcrumbs);

    return () => {
      setContentClassName(null);
      setHeaderContent(null);
    };
  }, [breadcrumbs, setContentClassName, setHeaderContent]);

  return (
    <>
      <div className="w-full px-6 py-6">
        <ChampionshipEditFormTabs championship={champ} />
      </div>
      <ChampionshipEditForm championship={champ} />
    </>
  );
};

export default ChampionshipEditPage;
